import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { provideComponentStore } from '@ngrx/component-store';
import { Dictionary } from '@ngrx/entity/src/models';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import moment from 'moment-timezone';
import {
  OnRangeSelectedResult, UiButtonComponent,
  UiCalendarComponent,
  UiPreloaderComponent,
  UiToggleComponent,
  UiTrackerClassSelectorComponent,
} from '@ui';
import { PreloaderColor, UIInputStyle } from '@enums/shared.enum';
import { DashboardModel } from '@models/dashboard.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { selectUserHoursFormat } from '@states/user-settings/user-settings.selectors';
import { EdgeCamera } from '../../../../../../cameras/camera.model';
import { CameraPickerModels } from '../../../../../../modals/camera-picker-inline/models/models';
import { CameraPickerModalComponent } from '../../../../../../modals/camera-picker-modal/camera-picker-modal.component';
import { DashboardLayoutComponent } from '../../../../../../pages/dashboards/pages/dashboard-layout/dashboard-layout.component';
import { UiWidgetComponent } from '../../../../../ui-kit/ui-widget/ui-widget.component';
import { UtilsV2Service } from '../../../../../../services/utils-v2.service';
import { DashboardFiltersStore } from '../../../../../dashboard/state/dashboard-filters.store';

const SECONDS_PER_MINUTE = 60;
const TRACKER_CHANGE_DEBOUNCE_MS = 2000;

/**
 * Read-only dashboard detail rendered inside the Command+K overlay. It provides
 * its own DashboardFiltersStore, so the nested production widgets resolve this
 * scoped store through the injector and their filter/refresh activity stays
 * isolated from any dashboard page open behind the modal. The dashboard entity
 * itself is loaded upstream (intelligence detail request); this component only
 * owns the render session.
 */
@Component({
  selector: 'app-dashboard-detail',
  standalone: true,
  templateUrl: './dashboard-detail.component.html',
  styleUrl: './dashboard-detail.component.scss',
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    MatSelectModule,
    MatTooltip,
    MatRippleModule,
    TranslatePipe,
    UiTrackerClassSelectorComponent,
    UiCalendarComponent,
    UiToggleComponent,
    DashboardLayoutComponent,
    UiWidgetComponent,
    UiButtonComponent,
    UiPreloaderComponent,
  ],
  providers: [provideComponentStore(DashboardFiltersStore)],
})
export class DashboardDetailComponent implements OnChanges, OnInit {
  @Input() public dashboard: IntelligenceModels.Dashboards.Detail;

  @ViewChild('wrapper') private wrapper: ElementRef<HTMLElement>;

  public readonly inputStyles: typeof UIInputStyle = UIInputStyle;
  public readonly PreloaderColor: typeof PreloaderColor = PreloaderColor;
  public readonly DetailViewMode: typeof DashboardModel.DetailViewMode = DashboardModel.DetailViewMode;
  public readonly viewMode: WritableSignal<DashboardModel.DetailViewMode> =
    signal<DashboardModel.DetailViewMode>(DashboardModel.DetailViewMode.Grid);
  public readonly maxRetentionDays = this.store$.selectSignal(OrganizationSelectors.selectMaxRetentionDays);
  public readonly hoursFormat = this.store$.selectSignal(selectUserHoursFormat);

  // View-model mirrors of the scoped store, kept in sync for template binding.
  public dateRange: OnRangeSelectedResult;
  public trackerOptions: DashboardModel.YAxisTrackerOptions;
  public selectedCameras: EdgeCamera.CameraItem[] = [];
  public autoRefreshEnabled: boolean = false;
  public autoRefreshInSec: number = 30;
  public remainingSeconds: number = -1;
  public widgetsLoading: boolean = false;

  private readonly trackerOptionsDebouncer = new Subject<void>();

  constructor(
    private readonly filtersStore: DashboardFiltersStore,
    private readonly store$: Store,
    private readonly dialog: MatDialog,
    private readonly utilsService: UtilsV2Service,
    private readonly destroyRef: DestroyRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboard'] && this.dashboard) {
      this.filtersStore.initialize({
        dashboardId: this.dashboard.id,
        filters: this.dashboard.filters,
      });
    }
  }

  public ngOnInit(): void {
    this.trackerOptionsDebouncer
      .pipe(debounceTime(TRACKER_CHANGE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.filtersStore.setTrackerOptions(this.trackerOptions));

    this.filtersStore.runtimeFilters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.dateRange = filters.dateRange;
        this.trackerOptions = filters.trackerOptions;
      });
    this.filtersStore.selectedCameras$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cameras) => (this.selectedCameras = cameras));
    this.filtersStore.autoRefreshEnabled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => (this.autoRefreshEnabled = enabled));
    this.filtersStore.autoRefreshIntervalSec$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((interval) => (this.autoRefreshInSec = interval));
    this.filtersStore.remainingSeconds$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((seconds) => (this.remainingSeconds = seconds));
    this.filtersStore.widgetsLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => (this.widgetsLoading = loading));
  }

  public get hasWidgets(): boolean {
    return !!this.dashboard?.layout?.length;
  }

  public setViewMode(mode: DashboardModel.DetailViewMode): void {
    this.viewMode.set(mode);
  }

  public selectCamera(): void {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: {
        multi: true,
        edgeCameraEnabled: true,
        selectedValueInputType: CameraPickerModels.SelectedCameraInputType.EdgeItem,
      },
      selectedValue: this.selectedCameras,
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .subscribe((selectedCameras: Dictionary<CameraPickerModels.SelectedCamera>) => {
        if (selectedCameras) {
          this.filtersStore.setCameras(selectedCameras);
        }
      });
  }

  public changeTracker(): void {
    this.trackerOptionsDebouncer.next();
  }

  public changeDateRange(event: OnRangeSelectedResult): void {
    this.filtersStore.setDateRange(event);
  }

  public resetFilters(): void {
    this.filtersStore.reset();
  }

  public saveFilters(): void {
    this.filtersStore.saveFilters();
  }

  public configureAutoRefresh(): void {
    this.filtersStore.configureAutoRefresh({
      enabled: this.autoRefreshEnabled,
      intervalSec: this.autoRefreshInSec,
    });
  }

  public refresh(): void {
    this.filtersStore.refresh();
  }

  public userTimezoneAbbreviation(): string {
    return moment().tz(moment.tz.guess()).format('z');
  }

  public get isFullscreen(): boolean {
    return this.utilsService.isFullscreen();
  }

  public async maximize(): Promise<void> {
    const elem: HTMLElement & {
      msRequestFullscreen?: () => Promise<void>;
      mozRequestFullScreen?: () => Promise<void>;
      webkitRequestFullscreen?: () => Promise<void>;
    } = this.wrapper?.nativeElement;
    if (!this.isFullscreen) {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        await elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    }
  }

  public get remainingTimeDisplay(): string {
    if (this.remainingSeconds <= 0) {
      return '00:00';
    }
    const minutes = Math.floor(this.remainingSeconds / SECONDS_PER_MINUTE);
    const seconds = this.remainingSeconds % SECONDS_PER_MINUTE;
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }
}
